'use client';

import React, { useState, useEffect } from 'react';
import { AxiosPost } from '@/utilities/axioscall';
import { AxiosResponse } from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { FaFacebookF, FaInstagram, FaTwitter, FaYoutube } from 'react-icons/fa';
import { IoLogoLinkedin } from 'react-icons/io5';
import { HiArrowUpRight } from 'react-icons/hi2';
import { FadeLoader } from 'react-spinners';

// Define TypeScript interface for category data
interface Category {
  id: number;
  parent_id: number;
  name: string;
  seo_url: string;
}

interface FooterProps {
  categoryListData: Category[];
}

const Footer: React.FC<FooterProps> = ({ categoryListData }) => {
  const [isLargeScreen, setIsLargeScreen] = useState(true);
  const [formValues, setFormValues] = useState({
    newsemail: '',
    newsemailError: false,
    formloading: false,
  });

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const mainCategories = categoryListData
    ?.filter((category) => category.parent_id === 0)
    .sort((a, b) => a.id - b.id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const email = formValues.newsemail?.trim();

    if (!email) {
      setFormValues((prev) => ({ ...prev, newsemailError: true }));
      return;
    }

    setFormValues((prev) => ({ ...prev, newsemailError: false, formloading: true }));

    try {
      const formData = { email };
      const res = await AxiosPost('AK.NEWSLETTERFORMAPI', formData);

      if (res && typeof res !== 'string' && res.data) {
        toast.success('Newsletter subscribed successfully.', {
          style: { border: '1px solid #00b852', padding: '16px', color: '#00b852' },
          iconTheme: { primary: '#00b852', secondary: '#FFFAEE' },
        });
        setFormValues({ newsemail: '', newsemailError: false, formloading: false });
      } else {
        toast.error('Newsletter validation error.', {
          style: { border: '1px solid #cc0000', padding: '16px', color: '#cc0000' },
          iconTheme: { primary: '#cc0000', secondary: '#FFFAEE' },
        });
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      toast.error('Failed to subscribe. Please try again.', {
        style: { border: '1px solid #cc0000', padding: '16px', color: '#cc0000' },
        iconTheme: { primary: '#cc0000', secondary: '#FFFAEE' },
      });
    } finally {
      setFormValues((prev) => ({ ...prev, formloading: false }));
    }
  };


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <div className="!bg-[#f0f0f0] !py-8 !w-full !mt-[16px] ">
        <div className='!max-w-7xl !mx-auto'>
          <div className=" !mx-auto !grid !grid-cols-1 sm:!grid-cols-2 md:!grid-cols-4 lg:!grid-cols-5 xl:!grid-cols-6 !gap-6 !px-4">
            <div>
              <h4 className="!text-lg !font-bold !mb-4 !text-gray-800 !relative before:!content-none">CATEGORIES</h4>
              <ul className="!list-none !p-0 !m-0">
                {mainCategories?.length > 0 ? (
                  mainCategories.map((category) => (
                    <li key={category.id} className="!mb-2 !relative before:!content-none">
                      <a
                        href={`/products/${category.seo_url}`}
                        className="!text-gray-600 !no-underline hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                      >
                        {category.name}
                      </a>
                    </li>
                  ))
                ) : (
                  <p className="!text-gray-600 !relative before:!content-none">Loading categories...</p>
                )}
              </ul>
            </div>
            <div>
              <h4 className="!text-lg !font-bold !mb-4 !text-gray-800 !relative before:!content-none">POLICIES</h4>
              <ul className="!list-none !p-0 !m-0">
                <li className="!mb-2 !relative before:!content-none">
                  <a
                    href="/privacypolicy"
                    className="!text-gray-600 !no-underline hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li className="!mb-2 !relative before:!content-none">
                  <a
                    href="/shippingandrefundpolicy"
                    className="!text-gray-600 !no-underline hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                  >
                    Return Policy
                  </a>
                </li>
                <li className="!mb-2 !relative before:!content-none">
                  <a
                    href="/termsandcondition"
                    className="!text-gray-600 !no-underline hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                  >
                    Terms & Conditions
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="!text-lg !font-bold !mb-4 !text-gray-800 !relative before:!content-none">ORDER</h4>
              <ul className="!list-none !p-0 !m-0">
                <li className="!mb-2 !relative before:!content-none">
                  <a
                    href="/trackorder"
                    className="!text-gray-600 !no-underline hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                  >
                    Track Order
                  </a>
                </li>
                <li className="!mb-2 !relative before:!content-none">
                  <a
                    href="/myorders"
                    className="!text-gray-600 !no-underline hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                  >
                    My Order
                  </a>
                </li>
                <li className="!mb-2 !relative before:!content-none">
                  <a
                    href="/sizechart"
                    className="!text-gray-600 !no-underline hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                  >
                    Size Chart
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="!text-lg !font-bold !mb-4 !text-gray-800 !relative before:!content-none">QUICK LINKS</h4>
              <ul className="!list-none !p-0 !m-0">
                <li className="!mb-2 !relative before:!content-none">
                  <a
                    href="/aboutus"
                    className="!text-gray-600 !no-underline hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                  >
                    About Us
                  </a>
                </li>
                <li className="!mb-2 !relative before:!content-none">
                  <a
                    href="/contactus"
                    className="!text-gray-600 !no-underline hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                  >
                    Contact Us
                  </a>
                </li>
                <li className="!mb-2 !relative before:!content-none">
                  <a
                    href="/blog"
                    className="!text-gray-600 !no-underline hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                  >
                    Blog
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="!text-lg !font-bold !mb-4 !text-gray-800 !relative before:!content-none">SOCIAL</h4>
              <div className="!flex !gap-4 !relative before:!content-none">
                <a
                  href="https://www.facebook.com/profile.php?id=61566032990704"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="!text-gray-600 hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                >
                  <FaFacebookF size={22} />
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="!text-gray-600 hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                >
                  <FaTwitter size={22} />
                </a>
                <a
                  href="https://www.instagram.com/zemdrop.shop/profilecard/?igsh=MXdvaDI3bnVpYWM0Yw=="
                  target="_blank"
                  rel="noopener noreferrer"
                  className="!text-gray-600 hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                >
                  <FaInstagram size={22} />
                </a>
                <a
                  href="https://www.linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="!text-gray-600 hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
                >
                  <IoLogoLinkedin size={22} />
                </a>
              </div>
            </div>
            <div>
              <h4 className="!text-lg !font-bold !mb-4 !text-gray-800 !relative before:!content-none">SUBSCRIBE FOR OFFERS!</h4>
              <div className="!flex !items-center !w-full !gap-2 !border !border-[#000000]">
                <input
                  id="newsemail"
                  name="newsemail"
                  value={formValues.newsemail}
                  onChange={handleChange}
                  type="email"
                  placeholder="Enter Your Email"
                  className="!p-2 !rounded !w-full !flex-grow before:!content-none !bg-gray-50"
                />
                <button onClick={handleSubmit} className="!relative !p-2 !bg-black !text-white !hover:bg-blue-700 !transition-colors !flex !items-center !justify-center !w-10 !h-10">
                  {/* Icon - fades out when loading */}
                  <HiArrowUpRight
                    className={`!text-lg !transition-opacity !duration-200 ${formValues.formloading ? '!opacity-0' : '!opacity-100'
                      }`}
                  />

                  {/* Loader - fades in when loading */}
                  <div
                    className={`!absolute !inset-0 !flex !items-center !justify-center !transition-opacity !duration-200 ${formValues.formloading ? '!opacity-100' : '!opacity-0'
                      }`}
                  >
                    <FadeLoader
                      loading={formValues.formloading}
                      color="rgb(84 117 229)"
                      height={10}
                      width={2}
                      radius={1}
                      margin={-3}
                    />
                  </div>
                </button>

              </div>
              {formValues.newsemailError && (
                <p className="!text-red-500 !mt-2 !relative before:!content-none">Please enter your email.</p>
              )}
            </div>
          </div>
          <div className="container !mx-auto !px-4 !mt-6 !relative before:!content-none">
            <p className="!text-[17px] !flex !flex-wrap !gap-1 !text-gray-800 !relative before:!content-none">
              <span className="!font-semibold">Legal Entity:</span> Logeshwaran
            </p>
          </div>

        </div>
      </div>
      <div className="!py-4 !text-center !text-gray-600 !relative before:!content-none">
        <p>
          © {new Date().getFullYear()} Zemdrop. All rights reserved. Developed & Maintained with ❤️{' '}
          <a
            href="https://www.vilvabusiness.com/ecomsaas"
            target="_blank"
            rel="noopener noreferrer"
            className="!text-gray-600 !no-underline hover:!text-black hover:!scale-105 !transition-transform !duration-200 !relative before:!content-none"
          >
            Vilva x EcomSaas
          </a>.
        </p>
      </div>
    </>
  );
};

export default Footer;